'use client'

import React, { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from 'features/store'
import { setUser } from 'features/authSlice'
import Image from 'next/image'
import { Lightbulb } from 'lucide-react'
import ProfileCompletedModal from '@/components/modals/ProfileCompletedModal'
import { Listing, useUpdateProfileMutation } from 'graphql/generated/graphql'

const COMPLETED_DATE_KEY = 'culineer_producer_profile_completed_date'
const WIDGET_DISMISSED_KEY = 'culineer_producer_profile_widget_dismissed'
const MODAL_SHOWN_KEY = 'culineer_producer_profile_completed_modal_shown'
const COMPLETED_WIDGET_DAYS = 5

// TODO: confirm exact wording with Jodi — source Figma text was too small to
// transcribe with full confidence.
const PRO_TIP_TEXT = 'Profiles with active recipes get 3x more views and direct farm inquiries.'

type ProducerProfileCompletionWidgetProps = {
    listing?: Pick<Listing,
        | 'slug'
        | 'user_type'
        | 'business_description_completed'
        | 'products_completed'
        | 'selling_locations_completed'
        | 'sales_channels_completed'
        | 'profile_image_completed'
        | 'geolocation_completed'
    > | null
}

const ProducerProfileCompletionWidget = ({ listing }: ProducerProfileCompletionWidgetProps) => {
    const router = useRouter()
    const dispatch = useDispatch()
    const user = useSelector((state: RootState) => state.culineerAuth.user)
    const [updateProfile] = useUpdateProfileMutation()
    const serverDismissed = !!user?.profile_widget_dismissed
    const [showModal, setShowModal] = useState(false)
    const [dismissed, setDismissed] = useState(false)
    const prevCompleteRef = useRef<boolean | null>(null)

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setDismissed(localStorage.getItem(WIDGET_DISMISSED_KEY) === 'true')
        }
    }, [])

    const steps = [
        { id: 'business_description', label: 'Add business description', completed: !!listing?.business_description_completed },
        { id: 'products', label: 'Add products', completed: !!listing?.products_completed },
        { id: 'selling_locations', label: 'Add selling locations', completed: !!listing?.selling_locations_completed },
        { id: 'sales_channels', label: 'Add sales channels', completed: !!listing?.sales_channels_completed },
        { id: 'profile_image', label: 'Upload profile image', completed: !!listing?.profile_image_completed },
        { id: 'location', label: 'Add your location', completed: !!listing?.geolocation_completed },
    ]

    const completedCount = steps.filter(s => s.completed).length
    const isFullyComplete = completedCount === steps.length
    const progress = (completedCount / steps.length) * 100

    // Detect transition to fully complete → show congrats modal once, ever
    useEffect(() => {
        if (!listing) return
        if (prevCompleteRef.current === null) {
            prevCompleteRef.current = isFullyComplete
            if (
                isFullyComplete &&
                typeof window !== 'undefined' &&
                !localStorage.getItem(COMPLETED_DATE_KEY)
            ) {
                // Already complete the first time this widget ever mounts (e.g. the
                // profile was finished before this widget existed, or in another
                // browser) -- backfill the completion date so the grace-period
                // countdown below can actually start instead of never expiring.
                localStorage.setItem(COMPLETED_DATE_KEY, Date.now().toString())
            }
            return
        }
        if (prevCompleteRef.current === false && isFullyComplete) {
            const alreadyShown = typeof window !== 'undefined' &&
                localStorage.getItem(MODAL_SHOWN_KEY) === 'true'
            if (!alreadyShown) {
                localStorage.setItem(COMPLETED_DATE_KEY, Date.now().toString())
                localStorage.setItem(MODAL_SHOWN_KEY, 'true')
                setShowModal(true)
            }
        }
        prevCompleteRef.current = isFullyComplete
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listing, isFullyComplete])

    // Save the dismissal on the account so it follows the user to other browsers/devices.
    const persistDismissed = () => {
        if (!user?.user_id || user.profile_widget_dismissed) return
        dispatch(setUser({ profile_widget_dismissed: true }))
        updateProfile({
            variables: { userID: user.user_id, input: { profile_widget_dismissed: true } as any },
        }).catch((error) => console.error('Failed to save widget dismissal:', error))
    }

    // Dismissed earlier in this browser but never saved to the account -- save it now.
    useEffect(() => {
        if (dismissed) persistDismissed()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dismissed, user?.user_id])

    // Start the 5-day "Profile Completed" countdown on the account, once, so a new browser or
    // device doesn't restart it. Reuse this browser's date if it has one so a running
    // countdown isn't extended.
    useEffect(() => {
        if (!isFullyComplete || !user?.user_id || user.profile_widget_completed_at || typeof window === 'undefined') return
        const local = parseInt(localStorage.getItem(COMPLETED_DATE_KEY) || '', 10)
        const stamp = new Date(Number.isFinite(local) ? local : Date.now()).toISOString()
        dispatch(setUser({ profile_widget_completed_at: stamp }))
        updateProfile({
            variables: { userID: user.user_id, input: { profile_widget_completed_at: stamp } as any },
        }).catch((error) => console.error('Failed to save profile completion date:', error))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFullyComplete, user?.user_id, user?.profile_widget_completed_at])

    if (!listing || dismissed || serverDismissed) return null

    const handleDismiss = () => {
        localStorage.setItem(WIDGET_DISMISSED_KEY, 'true')
        setDismissed(true)
        persistDismissed()
    }

    const handleContinueSetup = () => {
        const userType = listing.user_type?.toLowerCase()
        if ((userType === 'producer' || userType === 'market') && listing.slug) {
            router.push(`/${userType}/${listing.slug}`)
        }
    }

    const completedDateRaw = typeof window !== 'undefined'
        ? localStorage.getItem(COMPLETED_DATE_KEY)
        : null
    // The account's date wins (so every browser agrees); fall back to this browser's.
    const accountCompletedAt = user?.profile_widget_completed_at
        ? new Date(user.profile_widget_completed_at).getTime()
        : NaN
    const completedDate = Number.isFinite(accountCompletedAt)
        ? accountCompletedAt
        : completedDateRaw ? parseInt(completedDateRaw, 10) : null
    const daysSinceCompletion = completedDate
        ? (Date.now() - completedDate) / (1000 * 60 * 60 * 24)
        : null

    // Fully complete: pro-tip and widget both disappear (after a short grace window
    // during which a "Profile Completed" confirmation card is still shown, matching
    // the general-member widget's behavior).
    if (isFullyComplete) {
        if (completedDate && daysSinceCompletion !== null && daysSinceCompletion > COMPLETED_WIDGET_DAYS) {
            return null
        }
        return (
            <>
                <ProfileCompletedModal open={showModal} onClose={() => setShowModal(false)} />
                <div className="relative mb-4 rounded-xl border border-[#E6E6E6] bg-white p-3 shadow-sm">
                    <div className="mb-1 flex items-start justify-between gap-2">
                        <div className="flex min-w-0 flex-1 items-center gap-3">
                            <Image
                                src="/assets/icons/profile-complete-badge.png"
                                alt="Profile complete"
                                width={40}
                                height={40}
                                className="flex-shrink-0"
                            />
                            <div>
                                <p className="text-sm font-semibold text-gray-900">Profile Completed</p>
                                <p className="text-xs text-gray-500">Your business profile is fully set up.</p>
                            </div>
                        </div>
                        <button
                            onClick={handleDismiss}
                            className="relative z-10 flex-shrink-0 text-gray-300 hover:text-gray-500"
                            aria-label="Dismiss"
                        >
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <path d="M9 3L3 9M3 3l6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                        </button>
                    </div>
                </div>
            </>
        )
    }

    return (
        <>
            <ProfileCompletedModal open={showModal} onClose={() => setShowModal(false)} />

            {/* Pro tip — shown together with the widget, hidden together once complete */}
            <div className="mb-2 flex items-start gap-2 rounded-lg bg-[#EAF6D6] p-2.5">
                <Lightbulb size={16} className="mt-0.5 flex-shrink-0 text-[#548828]" />
                <p className="text-xs leading-snug text-[#3F5C22]">
                    <span className="font-semibold">Pro tip:</span> {PRO_TIP_TEXT}
                </p>
            </div>

            <div className="relative mb-4 rounded-xl border border-[#D2D2D2] bg-white p-3 shadow-sm">
                <div className="mb-2 flex items-center gap-2">
                    <span className="min-w-0 flex-1 font-['Inter'] text-base font-semibold leading-[150%] tracking-[-0.02em] text-[#202020]">
                        Complete Profile
                    </span>
                    <span className="flex-shrink-0 font-['Inter'] text-base font-normal leading-[150%] tracking-[-0.02em] text-[#6B6B6B]">
                        Step {completedCount} of {steps.length}
                    </span>
                    <button
                        onClick={handleDismiss}
                        className="relative z-10 flex-shrink-0 text-gray-300 hover:text-gray-500"
                        aria-label="Dismiss"
                    >
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M9 3L3 9M3 3l6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                    </button>
                </div>

                <div className="mb-4 h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                    <div
                        className="h-full rounded-full bg-[#85C349] transition-all duration-500"
                        style={{ width: `${progress}%` }}
                    />
                </div>

                <ul className="mb-4 space-y-2.5">
                    {steps.map((step) => (
                        <li key={step.id}>
                            <div className="flex items-center gap-2.5">
                                {step.completed ? (
                                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" className="flex-shrink-0">
                                        <circle cx="9" cy="9" r="9" fill="#85C349" />
                                        <path d="M5.5 9l2.5 2.5 4.5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                ) : (
                                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" className="flex-shrink-0">
                                        <circle cx="9" cy="9" r="8" stroke="#D1D5DB" strokeWidth="1.5" fill="none"/>
                                    </svg>
                                )}
                                <span className={`text-xs ${step.completed ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                                    {step.label}
                                </span>
                            </div>
                        </li>
                    ))}
                </ul>

                <button
                    onClick={handleContinueSetup}
                    className="w-full rounded-full bg-[#85C349] py-2 text-xs font-semibold text-white transition hover:bg-[#75B339]"
                >
                    Complete Profile
                </button>
            </div>
        </>
    )
}

export default ProducerProfileCompletionWidget
